import i18n from '@/i18n';
import { filterCatalogProducts } from '@/pages/menu/catalog/catalogHelpers';

const PROMO_TYPE_LABELS = {
  daily: 'Promo del Día',
  duo: 'Promo Dúo',
  group: 'Promo Grupal',
  family: 'Promo Familiar',
  hidden: 'Promo Oculta',
  discount: 'Descuento',
  '2x1': '2x1',
  combo: 'Combo',
  bundle: 'Combo',
  happy_hour: 'Happy Hour',
  percentage: 'Descuento',
};

export function getCatalogPromoTypeLabel(type) {
  const key = String(type || '').trim();
  const fallback = PROMO_TYPE_LABELS[key] || 'Promoción';
  if (!key) return i18n.t('promos.fallback', { defaultValue: fallback });
  return i18n.t(`promos.${key}`, {
    defaultValue: fallback,
  });
}

export function isCatalogPromoExpired(expiresAt) {
  if (!expiresAt) return false;
  const expiry = new Date(expiresAt);
  return Number.isNaN(expiry.getTime()) ? false : expiry < new Date();
}

/** Promos clásicas visibles en catálogo (excluye ocultas/sorpresa del asistente). */
export function isPromoVisibleInCatalog(promo) {
  if (!promo || promo.is_active === false) return false;
  if (isCatalogPromoExpired(promo.expires_at)) return false;
  if (promo.is_hidden || promo.promo_type === 'hidden') return false;
  if (promo.is_surprise) return false;
  const productIds = Array.isArray(promo.product_ids) ? promo.product_ids : [];
  if (productIds.length === 0) return false;
  return true;
}

export function resolveLinkedPromoProducts(promo, products) {
  const ids = Array.isArray(promo?.product_ids) ? promo.product_ids : [];
  const list = Array.isArray(products) ? products : [];
  return ids.map((id) => list.find((p) => p?.id === id)).filter(Boolean);
}

export function sumLinkedProductPrices(linkedProducts) {
  return linkedProducts.reduce(
    (sum, p) => sum + (Number(p?.final_price ?? p?.price) || 0),
    0
  );
}

export function resolvePromoDisplayPrice(promo, linkedProducts) {
  const special = Number(promo?.special_price);
  if (Number.isFinite(special) && special > 0) return special;

  const regularTotal = sumLinkedProductPrices(linkedProducts);
  if (promo?.promo_type === 'percentage') {
    const pct = Number(promo.discount_percentage);
    if (Number.isFinite(pct) && pct > 0 && regularTotal > 0) {
      return Math.round(regularTotal * (1 - pct / 100));
    }
  }
  if (promo?.promo_type === '2x1' && linkedProducts.length === 1) {
    return Number(linkedProducts[0]?.final_price ?? linkedProducts[0]?.price) || 0;
  }
  return regularTotal;
}

export function promoToCatalogItem(promo, products) {
  const linked = resolveLinkedPromoProducts(promo, products);
  const displayPrice = resolvePromoDisplayPrice(promo, linked);
  const regularTotal = sumLinkedProductPrices(linked);
  const photo = promo.photo_url || linked.find((p) => p.photo_url)?.photo_url || null;

  return {
    id: `catalog-promo-${promo.id}`,
    name: promo.name,
    description: promo.description || '',
    photo_url: photo,
    price: displayPrice,
    final_price: displayPrice,
    compare_at_price: regularTotal > displayPrice ? regularTotal : null,
    availability: 'active',
    is_catalog_promo: true,
    catalog_promo_id: promo.id,
    catalog_promo_type: promo.promo_type,
    catalog_promo: promo,
    catalog_promo_products: linked,
  };
}

function promoMatchesFilters(promo, linkedProducts, selectedCategory, searchQuery) {
  const query = String(searchQuery || '').toLowerCase().trim();
  if (query) {
    const blob = `${promo.name || ''} ${promo.description || ''}`.toLowerCase();
    const productHit = linkedProducts.some((p) => {
      const name = String(p?.name || '').toLowerCase();
      const desc = String(p?.description || '').toLowerCase();
      return name.includes(query) || desc.includes(query);
    });
    if (!blob.includes(query) && !productHit) return false;
  }
  if (selectedCategory && selectedCategory !== 'all') {
    return linkedProducts.some(
      (p) => (p.category_id || p.categoryId) === selectedCategory
    );
  }
  return true;
}

function mergePromosIntoProductList(productItems, promoItems) {
  if (!promoItems.length) return productItems;
  const dealIdx = productItems.findIndex((p) => p?.is_daily_deal);
  if (dealIdx >= 0) {
    return [
      ...productItems.slice(0, dealIdx + 1),
      ...promoItems,
      ...productItems.slice(dealIdx + 1),
    ];
  }
  return [...promoItems, ...productItems];
}

/**
 * Lista unificada para el grid del catálogo: Oferta del Día + promos clásicas + productos.
 */
export function buildCatalogDisplayItems({
  products,
  promos = [],
  selectedCategory,
  searchQuery,
  dailyDealProduct,
  bestsellerProduct,
  isDemo,
}) {
  const productItems = filterCatalogProducts({
    products,
    selectedCategory,
    searchQuery,
    dailyDealProduct,
    bestsellerProduct,
    isDemo,
  });

  const visiblePromos = (Array.isArray(promos) ? promos : []).filter((promo) =>
    isPromoVisibleInCatalog(promo)
  );

  const promoItems = visiblePromos
    .map((promo) => {
      const linked = resolveLinkedPromoProducts(promo, products);
      if (!linked.length) return null;
      if (!promoMatchesFilters(promo, linked, selectedCategory, searchQuery)) return null;
      return promoToCatalogItem(promo, products);
    })
    .filter(Boolean);

  return mergePromosIntoProductList(productItems, promoItems);
}

/** Producto sintético para línea de carrito con precio de promo. */
export function promoCatalogItemToCartProduct(item) {
  if (!item?.is_catalog_promo) return item;
  return {
    id: item.id,
    name: item.name,
    description: item.description,
    photo_url: item.photo_url,
    price: item.final_price,
    final_price: item.final_price,
    is_catalog_promo: true,
    catalog_promo_id: item.catalog_promo_id,
    catalog_promo_type: item.catalog_promo_type,
  };
}
